/**
 * A responsive image component that displays images with srcset support.
 * Uses the S3 bucket URL from environment variables and generates responsive image sources
 * from an array of thumbnail objects with different sizes.
 *
 * @example
 * ```tsx
 * <ResponsiveImage
 *   thumbnails={[
 *     { url: "ad-assets/thumbnails/image_240x240.jpg", width: 240, height: 240 },
 *     { url: "ad-assets/thumbnails/image_480x480.jpg", width: 480, height: 480 }
 *   ]}
 *   alt="Product image"
 *   className="rounded-md"
 *   sizes="(max-width: 768px) 240px, 480px"
 * />
 * ```
 */

interface Image {
    url: string
    width: number
    height: number
}

interface ResponsiveImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet'> {
    images: Image[]
    className?: string
    style?: React.CSSProperties
    sizes?: string
    alt?: string
}

const ResponsiveImage = ({ images, className = '', style, sizes = '100vw', alt = 'Image', ...props }: ResponsiveImageProps) => {
    const srcSet = images
        .map((thumb) => `${import.meta.env.VITE_S3_BUCKET_URL}${thumb?.url} ${thumb?.width}w`)
        .join(', ')

    return (
        <img
            alt={alt}
            className={className}
            src={`${import.meta.env.VITE_S3_BUCKET_URL}${images[0]?.url}`}
            sizes={sizes}
            srcSet={srcSet}
            style={style}
            {...props}
        />
    )
}

export default ResponsiveImage
